import olMap from 'ol/Map';
import BaseLayer from "ol/layer/Base";
import LayerGroup from 'ol/layer/Group';
import { BaseLayerFactory, BaseLayerType } from "../BaseLayerFactory";
import { BingSession } from "../BingSession";
import { MapViewStore } from "../MapViewStore";
import TrailwiseEventBus from '../TrailwiseEventBus';
import { BaseLayerHealthMonitor } from './BaseLayerHealthMonitor';

/**
 * Owns the base layer group: OSM contours / OS Open / historical maps and the
 * member-only Bing layers, radio binding, the zoom-driven Bing OS/Road/OSOpen
 * swap and the tracking-mode base layer lock.
 */
export class BaseLayerController {

    private _baseLayerFactory: BaseLayerFactory;
    private _BingOS: BaseLayer;
    private _BingRoad: BaseLayer;
    private _OSOpen: BaseLayer;
    private _OSMContoured: BaseLayer;
    private _selectedBaseLayerRadioId: string = "";
    private _healthMonitor: BaseLayerHealthMonitor;

    /** The contoured OSM base layer — AppGeoLocation forces this during tracking. */
    get osmContouredLayer(): BaseLayer {
        return this._OSMContoured;
    }

    public constructor(
        private _map: olMap,
        private _mapStore: MapViewStore,
        private _isMember: boolean,
        private _bingSession: BingSession,
        private _bingApiOrSessionKey: string,
        private _osmContouredApiKey: string
    ) {
        const self = this;

        this.addBaseLayers();

        TrailwiseEventBus.trackingStart.on(function () {
            self.onTrackingStarted();
        });
        TrailwiseEventBus.trackingEnd.on(function () {
            self.onTrackingEnded();
        });
    }

    /** Hooked to MapCore.onZoomChanged so the Bing layer swap tracks the view. */
    public handleZoomChanged = (zoom: number): void => {
        this.setBingOSView(zoom);
    }

    private addBaseLayers = (): void => {
        let self = this;

        this._baseLayerFactory = new BaseLayerFactory();

        let layers = [
            this._OSMContoured = this._baseLayerFactory.GetBaseLayerByType(BaseLayerType.OSM_ESDM_contours, "", this._osmContouredApiKey).olLayer,
            this._OSOpen = this._baseLayerFactory.GetBaseLayerByType(BaseLayerType.OS_Open_Data).olLayer,
            this._baseLayerFactory.GetBaseLayerByType(BaseLayerType.OSOneInch_Hills1885).olLayer,
            this._baseLayerFactory.GetBaseLayerByType(BaseLayerType.OSOneInch_1955To1961).olLayer,
            this._baseLayerFactory.GetBaseLayerByType(BaseLayerType.OSProvisional_1937to1961).olLayer,
            this._baseLayerFactory.GetBaseLayerByType(BaseLayerType.OS_Six_Inch_1888to1913).olLayer
        ];

        let mapSettings = this._mapStore.getMapInfo();

        if (this._isMember) {

            if (this._bingSession !== null) {
                //add Bing layers for auth users
                this._BingOS = this._baseLayerFactory.GetBaseLayerByType(BaseLayerType.BingOS, this._bingApiOrSessionKey).olLayer;
                this._BingRoad = this._baseLayerFactory.GetBaseLayerByType(BaseLayerType.BingRoad, this._bingApiOrSessionKey).olLayer;

                layers.push(this._BingOS);
                layers.push(this._BingRoad);
                layers.push(this._baseLayerFactory.GetBaseLayerByType(BaseLayerType.BingAerial, this._bingApiOrSessionKey).olLayer);
                layers.push(this._baseLayerFactory.GetBaseLayerByType(BaseLayerType.BingAerialWithLabels, this._bingApiOrSessionKey).olLayer);

                this._bingSession.setMap(this._map);
            }
            else {
                //hide Bing layer controls
                $("#liBingOS").hide();
                $("#liBingAerial").hide();
                $("#liBingAerialWithLabels").hide();

                mapSettings.baselayer = null;
            }
        }

        let layerGroup = new LayerGroup({
            layers: layers
        });
        let baseMapSet = false;

        layerGroup.getLayers().forEach(function (sublayer, j) {
            if (mapSettings.baselayer === sublayer.getProperties().name) {
                sublayer.setVisible(true);
                baseMapSet = true;
            }
        });

        //if the base isn't set then set the default
        if (!baseMapSet) {
            layerGroup.getLayers().forEach(function (sublayer, j) {
                if (sublayer.get("name") === "Contoured") {
                    sublayer.setVisible(true);
                    mapSettings.baselayer = "Contoured";
                }
            });
        }

        this._map.addLayer(layerGroup);
        this._healthMonitor = new BaseLayerHealthMonitor(layerGroup, this._map.getTargetElement() as HTMLElement);

        $("input[name='baseLayer']").each(function (i: number, e: HTMLElement) {
            $(this).prop("checked", mapSettings.baselayer === $(this).val());
            $(this).on("click", function (e) {
                self.baseRadioClicked(e);
            });
        });

        if (self._mapStore.getMapInfo().closePanelOnBaseLayerChange === true) {
            $("#ckAutoClose").prop("checked", true);
        }

        $("#ckAutoClose").on("click", function () {
            self._mapStore.onAutoCloseBaseLayerChanged($(this).prop("checked"));
        });
    }

    //Base layer changed handler
    private baseRadioClicked = (e: any): void => {
        let rdoVal = e.currentTarget.value;
        let self = this;

        this._map.getLayers().forEach(function (layer, i) {
            if (layer instanceof LayerGroup) {
                layer.getLayers().forEach(function (sublayer, j) {

                    let properties = sublayer.getProperties();
                    let visible = properties.name === rdoVal;
                    sublayer.setVisible(visible);

                    if (visible) {
                        //do this for the visible layer only
                        self._mapStore.onBaseLayerChanged(rdoVal, true);

                        if (self._mapStore.getMapInfo().closePanelOnBaseLayerChange === true) {
                            $("#slide-panel-btn-base-layers").trigger("click");
                        }
                    }
                });
            }
        });

        this.setBingOSView(this._map.getView().getZoom());
    }

    private onTrackingStarted = (): void => {
        const self = this;

        $("input[name='baseLayer']").each(function (index, element) {
            //remember the currently selected base layer
            if ($(this).prop("checked")) {
                self._selectedBaseLayerRadioId = element.id;
            }
            $(this).prop("disabled", element.id !== "rdoOpenContours");
        });
        $('#rdoOpenContours').trigger("click");
    }

    private onTrackingEnded = (): void => {
        const self = this;

        $("input[name='baseLayer']").each(function (index, element) {
            if (element.id !== "rdoOpenContours") {
                $(this).prop("disabled", false);
            }
        });

        if (self._selectedBaseLayerRadioId) {
            $(`#${self._selectedBaseLayerRadioId}`).trigger("click");
            self._selectedBaseLayerRadioId = "";
        }
    }

    private setBingOSView = (zoom: number): void => {
        const zoomInt: number = parseInt(zoom.toString());

        if (this._isMember && this._bingSession !== null && $('#rdoBingOS').prop("checked")) {
            //when minified we're not get whole number for the zoom level
            if (zoomInt <= 11 && this._BingOS.getVisible()) {
                //if current layer is BingOS then show BingRoad
                this._BingOS.setVisible(false);
                this._BingRoad.setVisible(true);
                this._OSOpen.setVisible(false);

            } else if (zoomInt > 11 && zoomInt < 17 && (this._BingRoad.getVisible() || this._OSOpen.getVisible())) {
                //if current layer is BingRoad then show BingOS
                this._BingOS.setVisible(true);
                this._BingRoad.setVisible(false);
                this._OSOpen.setVisible(false);

            } else if (zoomInt >= 17 && this._BingOS.getVisible()) {
                this._BingOS.setVisible(false);
                this._BingRoad.setVisible(false);
                this._OSOpen.setVisible(true);
            }
        }
    }
}
